import { useMemo, useState } from 'react';

import { useGlobalContext } from '../../../context/global';
import { usePeerChat } from '../../../hooks/usePeerChat';
import type { IAgentChatPanelData } from '../../../interface/peer-chat';
import type { ChatCounterpart } from '../../../interface/support-chat';
import { presenceLine } from '../../../lib/peer-chat-copy';
import { ActionSheet, type IActionSheetItem } from '../../utils/message-action-sheet';
import ChatConversation from './ChatConversation';
import ChatHeaderStrip from './ChatHeaderStrip';

/**
 * One conversation between an independent observer and their organisation's state observer.
 *
 * <p>The same engine and transcript as agent peer chat ({@link ChatConversation}), without any of
 * the party machinery around it: no unit coverage, no result chip, no party quick messages. VESR
 * proves the pair against api.core's observer hierarchy and keeps the thread inside the
 * organisation.</p>
 */
const ObserverChatPanel = () => {
	const { rsProps, peerInbox } = useGlobalContext();
	const data = (rsProps?.data ?? null) as IAgentChatPanelData | null;
	const counterpart = data?.counterpart;
	const thread = usePeerChat(counterpart?.coreUserId ?? 0, data?.electionId ?? 0);
	const [sheetOpen, setSheetOpen] = useState(false);

	const isSupervisor = counterpart?.role === 'parent';
	const roleLabel = isSupervisor ? 'Your state observer' : `Observer · ${counterpart?.level ?? 'Polling unit'}`;

	const chatCounterpart: ChatCounterpart = useMemo(
		() => ({ kind: 'peer', name: counterpart?.name || 'Observer', roleLabel }),
		[counterpart?.name, roleLabel]
	);

	if (!data || !counterpart || !rsProps) return null;
	if (!thread) {
		return (
			<div
				className='support-chat sc-loading'
				aria-busy='true'
			>
				<span className='visually-hidden'>Opening the conversation…</span>
			</div>
		);
	}

	const summary = peerInbox?.summaryFor(counterpart.coreUserId);
	const presence = summary?.counterpartLastSeenAt
		? presenceLine(
				{ lastSeenAt: summary.counterpartLastSeenAt, inThisThread: summary.counterpartInThreadKey === thread.threadKey },
				false
			)
		: '';

	const sheetItems: IActionSheetItem[] = [
		{
			key: 'call',
			label: counterpart.phone ? `Call ${counterpart.name}` : 'Call',
			disabled: !counterpart.phone,
			hint: counterpart.phone ? undefined : 'No phone number on file',
			onSelect: () => {
				if (counterpart.phone) window.location.href = `tel:${counterpart.phone}`;
			},
		},
		{
			key: 'desk',
			label: 'Message your observer desk',
			onSelect: () => rsProps.callSection({ action: 'view', component: 'support-chat', title: 'Observer desk' }),
		},
	];

	return (
		<div className='support-chat agent-chat'>
			<ChatHeaderStrip
				counterpart={counterpart}
				roleLabel={roleLabel}
				presence={presence}
				chip={{ label: counterpart.entityCode || 'Observer', tone: 'neutral' }}
				onMore={() => setSheetOpen(true)}
			/>
			<ChatConversation
				key={counterpart.coreUserId}
				thread={thread}
				counterpart={chatCounterpart}
				emptyState={{
					title: `Message ${counterpart.name}`,
					body: isSupervisor
						? 'Your state observer coordinates observers across the state. Tell them what you are seeing at your unit.'
						: 'You supervise this observer. Messages stay inside your organisation.',
				}}
				quickMessages={
					isSupervisor
						? ['I have arrived at my polling unit', 'Voting has started here', 'There is a problem at my unit']
						: ['Please send an update from your unit', 'Have you recorded your checklist?']
				}
			/>
			{sheetOpen ? (
				<ActionSheet
					label={`Actions for ${counterpart.name}`}
					items={sheetItems}
					onClose={() => setSheetOpen(false)}
				/>
			) : null}
		</div>
	);
};

export default ObserverChatPanel;
